import React, { useEffect, useState } from 'react'
import className from 'classnames/bind'

import style from './MyhouseManagementFee.module.scss'

import { realestateAxios } from '@Root/utils/realestateAxios'
import MyhouseCommonTitleLayout from '@Components/atoms/MyhouseCommonTitleLayout'
import MyhouseCommonTooltipLayer from '@Components/atoms/MyhouseCommonTooltipLayer'
import MyhouseManagementFeeCategory from '@Components/atoms/MyhouseManagementFeeCategory'
import MyhouseManagementFeeDetail from '@Components/atoms/MyhouseManagementFeeDetail'
import useManagementFee from '@Hooks/myhouse/managementfee/useManagementFee'
import useMain from '@Hooks/myhouse/main/useMain'
import { FETCH_STATUS } from '@Root/contants'
import ComplexPyeongDetail from '@Types/entities/ComplexPyeongDetail'
import { NCLICK } from '@Root/contants/lcs'
import { handleNclick } from '@Helpers/nclick'
import Banner from '@Root/types/Banner'
import MyhouseCommonBanner from '@Components/atoms/MyhouseCommonBanner'

const cx = className.bind(style)

const MyhouseManagementFee: React.FC = () => {
  const { reduxMain, refreshMasonryGrid } = useMain()
  const { managementFee, loadManagementFee, initializeManagementFee, setPyeongNo } = useManagementFee()
  const [isViewModePyeong, setIsViewModePyeong] = useState(false)
  const [isAverageExpanded, setIsAverageExpanded] = useState(false)
  const [isMonthlyExpanded, setIsMonthlyExpanded] = useState(false)
  const [banner, setBanner] = useState<{ isShow: boolean } & Pick<Banner, 'thumbnailUrl' | 'outLinkUrl'>>({
    isShow: false,
    thumbnailUrl: '',
    outLinkUrl: '',
  })

  const { house } = reduxMain
  const { pyeongNo, complexPyeongDetailList, complexPyeongDetailMap, isExistMaintenanceFee, fetchStatus: managementFeeFetchStatus } = managementFee

  const houseNo = house?.houseNo || 0
  const areaNo = house?.pyeongNo || 0
  const complexNo = house?.complexNo || 0
  const [currentComplexPyeongDetail, setCurrentComplexPyeongDetail] = useState<ComplexPyeongDetail>()

  const handleChangePyeong = (pyeongNo: string) => (e: React.SyntheticEvent) => {
    e.preventDefault()

    setPyeongNo(pyeongNo)
    refreshMasonryGrid()
  }
  const handleClickViewMode = (e: React.SyntheticEvent) => {
    e.preventDefault()
    handleNclick(isViewModePyeong ? NCLICK.MY_HOUSE.MAIN_MANAGEMENT_FEE.M2 : NCLICK.MY_HOUSE.MAIN_MANAGEMENT_FEE.PYEONG, e)
    setIsViewModePyeong(!isViewModePyeong)
  }

  const handleAverageButtonClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    !isAverageExpanded && handleNclick(NCLICK.MY_HOUSE.MAIN_MANAGEMENT_FEE.AVERAGE_MORE, e)

    setIsAverageExpanded(!isAverageExpanded)
    refreshMasonryGrid()
  }

  const handleMonthlyButtonClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    !isMonthlyExpanded && handleNclick(NCLICK.MY_HOUSE.MAIN_MANAGEMENT_FEE.MONTHLY_MORE, e)
    setIsMonthlyExpanded(!isMonthlyExpanded)
    refreshMasonryGrid()
  }

  const loadManagementFeeBanner = async () => {
    const response = await realestateAxios.get<Banner[]>(`/data-service/banner/type/${process.env.REACT_APP_MANAGEMENT_FEE_BANNER_TYPE_ID}`, {
      params: {
        bannerId: process.env.REACT_APP_MANAGEMENT_FEE_BANNER_ID,
      },
    })
    const banner = response?.data?.[0]
    if (banner) {
      setBanner({
        isShow: true,
        thumbnailUrl: banner?.thumbnailUrl ?? '',
        outLinkUrl: banner?.outLinkUrl ?? '',
      })
    }
  }

  useEffect(() => {
    initializeManagementFee()
    loadManagementFeeBanner()
  }, [])

  useEffect(() => {
    if (complexNo !== 0) {
      loadManagementFee(complexNo)
      return
    }

    initializeManagementFee()
  }, [complexNo])

  useEffect(() => {
    if (managementFeeFetchStatus === FETCH_STATUS.SUCCESS) {
      areaNo && setPyeongNo(String(areaNo))
    }
    refreshMasonryGrid()
  }, [houseNo, managementFeeFetchStatus])

  useEffect(() => {
    setCurrentComplexPyeongDetail(complexPyeongDetailMap?.[pyeongNo])
  }, [pyeongNo])

  if (!isExistMaintenanceFee || !['A01', 'A03', 'A04'].includes(house?.realestateTypeCode || '')) {
    initializeManagementFee()
    return null
  }

  if (managementFeeFetchStatus !== FETCH_STATUS.SUCCESS) {
    return null
  }

  return (
    <>
      <div className={cx('article')}>
        <MyhouseCommonTitleLayout title={'우리단지 관리비'} lineheight={26}>
          <MyhouseCommonTooltipLayer title={'단지 관리비'} styleTooltip={{ top: '19px', width: '100%', zIndex: 20 }} nclickOpen={NCLICK.MY_HOUSE.MAIN_MANAGEMENT_FEE.TOOLTIP}>
            우리집 서비스에서 제공하는
            <br />
            아파트단지 관리비 정보의 출처는 국토교통부이며,
            <br />
            동일 단지 내 동일 면적 관리비의 평균가로
            <br />
            실제 고지되는 관리비 금액과 차이가 있습니다.
          </MyhouseCommonTooltipLayer>
        </MyhouseCommonTitleLayout>
        <MyhouseManagementFeeCategory
          pyeongNo={pyeongNo}
          isViewModePyeong={isViewModePyeong}
          complexPyeongDetailList={complexPyeongDetailList}
          onViewModeButton={handleClickViewMode}
          onPyeongChangeButton={handleChangePyeong}
        />
        <MyhouseManagementFeeDetail
          isAverageExpanded={isAverageExpanded}
          isMonthlyExpanded={isMonthlyExpanded}
          onAverageButtonClick={handleAverageButtonClick}
          onMonthlyButtonClick={handleMonthlyButtonClick}
          averageMaintenanceCost={currentComplexPyeongDetail?.averageMaintenanceCost}
          maintenanceCostList={currentComplexPyeongDetail?.maintenanceCostList}
        />
      </div>
      {banner.isShow && (
        <MyhouseCommonBanner thumbnailUrl={banner.thumbnailUrl} outLinkUrl={banner.outLinkUrl} backgroundColor={'#ebfff0'} bannerAlt={'아파트 관리비 N페이 결제 안내'} />
      )}
    </>
  )
}

export default MyhouseManagementFee
